import React from 'react';
import { animated, OpaqueInterpolation, useSpring } from 'react-spring';

import { ContentCard, ContentCardIconType, VerticalSpacing, VerticalSpacingType } from 'ui/component';
import { SLIDE_SPEED_IN_MS, CARD_ANIMATION_SPEED_IN_MS } from '../../Constants';
import classNames from './EABDProcessAutomationContentCard.module.scss';

export interface ProcessAutomationContentCardProps {
  readonly isInFocus: boolean;
  readonly style?: React.CSSProperties;
}

export const ProcessAutomationContentCard = React.memo<ProcessAutomationContentCardProps>((props) => {
  const { isInFocus, style } = props;

  const [isCountAnimationFinished, setCountAnimationFinished] = React.useState(false);
  const automationCasesProps = useSpring({ count: isCountAnimationFinished ? 300 : 0 });
  const uniqueAutomationRulesProps = useSpring({ count: isCountAnimationFinished ? 6000 : 0 });
  const reusedAutomationRulesProps = useSpring({ count: isCountAnimationFinished ? 85 : 0 });

  const InterpolatorCallback = React.useCallback((countValue: number) => {
    const count = `${countValue.toFixed(0)}`;
    return `${count}+`;
  }, []);

  const PercentInterpolatorCallback = React.useCallback((countValue: number) => {
    const count = `${countValue.toFixed(0)}`;
    return `${count}%`;
  }, []);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout;

    const handler = () => {
      setCountAnimationFinished(true);
    };

    if (isInFocus) {
      const timeoutInMs = SLIDE_SPEED_IN_MS + CARD_ANIMATION_SPEED_IN_MS;
      timeout = setTimeout(handler, timeoutInMs);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isInFocus]);

  const BodyWithBottomSpacing = React.useMemo(() => {
    return (
      <React.Fragment>
        <p className={classNames.body}>
          {'End-to-end automation of operations & optimization tasks with run book automation and robotic process automation.'}
        </p>
        <VerticalSpacing type={VerticalSpacingType.X2} />
      </React.Fragment>
    );
  }, []);

  const RenderHeadlineCallback = React.useCallback((interpolation: OpaqueInterpolation<string>, caption: string) => {
    return (
      <div className={classNames.headlineListItem}>
        <animated.p className={classNames.headline}>{interpolation}</animated.p>
        <VerticalSpacing type={VerticalSpacingType.X05} />
        <p className={classNames.caption}>{caption}</p>
      </div>
    );
  }, []);

  return (
    <ContentCard title={'Process Automation'} iconType={ContentCardIconType.GEAR} isInFocus={isInFocus} style={style}>
      {BodyWithBottomSpacing}
      <div className={classNames.headlineList}>
        {RenderHeadlineCallback(automationCasesProps.count.interpolate(InterpolatorCallback), 'automation cases')}
        {RenderHeadlineCallback(uniqueAutomationRulesProps.count.interpolate(InterpolatorCallback), 'unique automation rules')}
        {RenderHeadlineCallback(
          reusedAutomationRulesProps.count.interpolate(PercentInterpolatorCallback),
          'reuse of automation rules across all customers'
        )}
      </div>
    </ContentCard>
  );
});
